import { Grid, Menu, MenuItem } from "@mui/material";
import { OLButton } from "OLButton";
import { OLClearIcon } from "components/CustomIcons/OLClearIcon";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import { Nullable } from "types/global";
import { TierDetailsType } from "types/tierSets";
import { useStyles } from "../TiersForm.styles";

interface Props {
  activeTier: number;
  tiers: TierDetailsType[];
  translations: {
    adminDefault?: boolean;
    apiDefault?: boolean;
    code: string;
    name: string;
    order: Nullable<number>;
  }[];
  areAllAvailableTranslationsAdded: boolean;
  setFieldValue: (key: string, value: any) => void;
}

export const Translations = ({
  activeTier,
  tiers,
  areAllAvailableTranslationsAdded,
  translations,
  setFieldValue,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const contextMenu = useContextMenu();

  const translationISOkeys = Object.keys(tiers[activeTier]?.translations);

  return (
    <>
      {translationISOkeys.map((iso, index: number) => {
        const isMoreThanOneLanguageAdded = index > 0;

        return (
          <section
            className={`${classes.translationSection} ${classes.conditionWrapper}`}
            key={index}
          >
            <Grid container spacing={16} wrap="nowrap">
              <Grid item xs={6}>
                <TextInput
                  className={classes.fullWidth}
                  data-test-id="name"
                  label={
                    index === 0 ? (
                      <RequiredFieldLabel
                        text={`${message({
                          id: "tierName",
                        })} (${iso})`}
                      />
                    ) : (
                      `${message({
                        id: "tierName",
                      })} (${iso})`
                    )
                  }
                  name={`tiers.${activeTier}.translations[${iso}].name`}
                />
              </Grid>
              <Grid item xs={isMoreThanOneLanguageAdded ? 5.5 : 6}>
                <TextInput
                  className={classes.fullWidth}
                  data-test-id="description"
                  label={`${message({
                    id: "tierDescription",
                  })} (${iso})`}
                  multiline
                  name={`tiers.${activeTier}.translations[${iso}].description`}
                />
              </Grid>
              {isMoreThanOneLanguageAdded && (
                <Grid item xs={0.5} className={classes.clearIconWidth}>
                  <OLClearIcon
                    data-test-id="clear"
                    onClick={() => {
                      const translationsToSave = tiers[activeTier].translations;
                      delete translationsToSave[iso];

                      setFieldValue(
                        `tiers.${activeTier}.translations`,
                        translationsToSave,
                      );
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </section>
        );
      })}

      {!areAllAvailableTranslationsAdded && (
        <OLButton
          className={classes.addTranslationBtn}
          color="secondary"
          data-test-id="add-translation"
          onClick={contextMenu.handleMenuOpen}
          startIcon={<PlusIcon />}
        >
          {message({ id: "translations" })}
        </OLButton>
      )}
      <Menu
        anchorEl={contextMenu.anchor}
        anchorOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        onClose={contextMenu.handleMenuClose}
        open={contextMenu.isMenuOpen}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        data-test-id="languages"
      >
        {translations
          .filter(
            ({ code }: { code: string }) =>
              !translationISOkeys.find((iso) => iso === code),
          )
          .map(({ code, name }: { code: string; name: string }) => (
            <MenuItem
              data-test-id="language"
              key={code}
              onClick={() => {
                contextMenu.handleMenuClose();
                setFieldValue(`tiers.${activeTier}.translations[${code}]`, {
                  name: "",
                  description: "",
                  locale: code,
                });
              }}
            >
              {name}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};
